import React, { forwardRef } from 'react';
import { cx, focusRing, motion } from './classes';

// Every button is amber or neutral, never coral. Hover styles use :not(:disabled) rather than
// Tailwind's enabled:, which only matches form controls and so would skip links.
const VARIANTS = {
  // The one main action in a view
  primary: 'bg-amber text-on-amber font-bold [&:not(:disabled)]:hover:bg-amber-hover',
  // The alternative next to a primary
  secondary: 'bg-transparent text-ink border border-control font-medium [&:not(:disabled)]:hover:bg-raised',
  // An amber outline for a secondary action that adds something, like Add clip in the sidebar
  outline: 'bg-transparent text-amber border border-amber font-bold [&:not(:disabled)]:hover:bg-raised',
  // Navigation inside a card, often with a trailing chevron
  text: 'bg-transparent text-amber font-bold [&:not(:disabled)]:hover:text-amber-hover',
  // 44 x 44 with an aria-label
  icon: 'w-target-min bg-transparent text-muted [&:not(:disabled)]:hover:bg-raised [&:not(:disabled)]:hover:text-ink',
};

// Height, padding and text size. Large is for the one big call to action on a marketing page.
const SIZES = {
  md: { primary: 'px-[22px]', secondary: 'px-[22px]', outline: 'px-space-4', text: 'px-space-1', icon: 'px-0' },
  lg: { primary: 'px-space-7', secondary: 'px-space-7', outline: 'px-space-6', text: 'px-space-1', icon: 'px-0' },
};

/**
 * A button, or a link when given `href` (or another component through `as`, such as a router Link).
 * size="lg" is for a marketing page's main call to action.
 * `icon` is a Lucide icon component shown before the label, or after it with iconPosition="end".
 */
export const Button = forwardRef(function Button(
  { variant = 'primary', size = 'md', icon: Icon, iconPosition = 'start', as, type, disabled = false, className, children, ...rest },
  ref
) {
  const Component = as || (rest.href ? 'a' : 'button');
  const isButton = Component === 'button';
  if (process.env.NODE_ENV !== 'production' && variant === 'icon' && !rest['aria-label']) {
    console.warn('Button: icon buttons need an aria-label');
  }
  const iconElement = Icon && (
    <Icon size={variant === 'icon' || size === 'lg' ? 20 : 16} strokeWidth={2.5} aria-hidden="true" className="flex-none" />
  );

  return (
    <Component
      ref={ref}
      type={isButton ? type || 'button' : type}
      disabled={isButton ? disabled : undefined}
      aria-disabled={!isButton && disabled ? 'true' : undefined}
      className={cx(
        'inline-flex items-center justify-center gap-space-2 rounded-radius-md font-text',
        size === 'lg' ? 'min-h-[56px] text-[18px] leading-[1.4]' : 'min-h-target-min text-body',
        'transition-colors no-underline',
        'disabled:opacity-40 disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:pointer-events-none',
        motion,
        focusRing,
        VARIANTS[variant],
        SIZES[size][variant],
        className
      )}
      {...rest}
    >
      {iconPosition === 'start' && iconElement}
      {children}
      {iconPosition === 'end' && iconElement}
    </Component>
  );
});
